import StyledContainer from '@design-system/styled/Container';
import { Trans, useTranslation } from 'next-i18next';
import styled from 'styled-components';

interface LocationData {
  name: string;
  address: string;
  image: string;
  gmapLink?: string;
}

interface VisitUsContent {
  title: string;
  subtitle: string;
  locations: LocationData[];
}

interface VisitUsProps {
  content: VisitUsContent;
}

const Container = styled(StyledContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #ffffff;
  width: 100%;
  gap: 23px;
  padding-top: 45px;
  padding-bottom: 45px;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    gap: 45px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 25px;
  width: 100%;
`;

const Title = styled.h2`
  ${(props) => props.theme.typo.familyGoogle.medium};
  color: ${(props) => props.theme.color.lightOrange};
  text-align: center;
  width: 100%;
  margin: 0;
  font-size: 22px;
  line-height: 1em;
  letter-spacing: normal;
  max-width: 292px;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    font-size: 33px;
    line-height: 0.97em;
    letter-spacing: -0.5%;
    max-width: 100%;
  }
`;

const Subtitle = styled.p`
  ${(props) => props.theme.typo.familyGoogle.regular};
  color: #363636;
  text-align: center;
  width: 100%;
  margin: 0;
  font-size: 14px;
  line-height: 1.43em;

  .link {
    color: ${(props) => props.theme.color.lightOrange};
    text-decoration: underline;
  }

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    font-size: 16px;
    line-height: 1.375em;
  }
`;

const LocationsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  width: fit-content;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 38px;
  }
`;

const LocationCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 324px;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    width: 285px;
  }

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.lg}) {
    width: 442px;
  }
`;

const LocationImage = styled.img`
  width: 100%;
  object-fit: cover;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background: #ff5722;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    height: 3px;
  }
`;

const LocationName = styled.div`
  ${(props) => props.theme.typo.familyGoogle.semiBold};
  font-size: 18px;
  line-height: 26px;
  color: #363636;

  a {
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    text-decoration: underline;
    color: ${(props) => props.theme.color.lightOrange};
  }

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    font-size: 22px;
    line-height: 26px;
  }
`;

const LocationAddress = styled.div`
  ${(props) => props.theme.typo.familyGoogle.regular};
  font-size: 14px;
  line-height: 20px;
  color: #363636;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const VisitUs: React.FC<VisitUsProps> = ({ content }) => {
  const { t } = useTranslation('sellCar');
  const { title, subtitle, locations } = content;
  const scrollToSellCarForm = () => {
    const element = document.getElementById('request-form-wrapper');

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  return (
    <Container>
      <Header>
        <Title>
          <Trans ns="sellCar" i18nKey={title} defaults={title} />
        </Title>
        <Subtitle>
          <Trans
            ns="sellCar"
            i18nKey={subtitle}
            defaults={subtitle}
            components={{ a: <a className="link" onClick={scrollToSellCarForm} /> }}
          />
        </Subtitle>
      </Header>
      <LocationsContainer>
        {locations.map((location: LocationData, index: number) => (
          <LocationCard key={index}>
            <LocationImage src={location.image} alt={t(location.name)} />
            <Divider />
            <div>
              <LocationName>
                {location.gmapLink ? (
                  <a href={location.gmapLink} target="_blank" rel="noreferrer">
                    <Trans ns="sellCar" i18nKey={location.name} defaults={location.name} />
                  </a>
                ) : (
                  <Trans ns="sellCar" i18nKey={location.name} defaults={location.name} />
                )}
              </LocationName>
              <LocationAddress>
                <Trans ns="sellCar" i18nKey={location.address} defaults={location.address} />
              </LocationAddress>
            </div>
          </LocationCard>
        ))}
      </LocationsContainer>
    </Container>
  );
};

export default VisitUs;
